import * as React from "react";
import { CheckIcon, ChevronsUpDown } from "lucide-react";
import * as RPNInput from "react-phone-number-input";
import flags from "react-phone-number-input/flags";
import { Input } from "../ui/input";
import { cn } from "../../lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Button } from "../ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/command";
import { ScrollArea } from "../ui/scroll-area";

type PhoneInputProps = Omit<
  React.ComponentProps<"input">,
  "onChange" | "value" | "ref"
> &
  Omit<RPNInput.Props<typeof RPNInput.default>, "onChange"> & {
    onChange?: (value: RPNInput.Value) => void;
    label?: string;
    error?: string;
  };

const PhoneInput: React.ForwardRefExoticComponent<PhoneInputProps> =
  React.forwardRef<React.ElementRef<typeof RPNInput.default>, PhoneInputProps>(
    ({ className, onChange, label, error, ...props }, ref) => {
      const inputRef = React.useRef<HTMLInputElement>(null);
      const prevValueRef = React.useRef<string>("");
      const cursorPosRef = React.useRef<number | null>(null);
      
      const countDigitsBeforePosition = (text: string, position: number): number => {
        return text.slice(0, position).replace(/\D/g, '').length;
      };
      
      const findPositionAfterDigits = (text: string, digitCount: number): number => {
        let digitsFound = 0;
        for (let i = 0; i < text.length; i++) {
          if (/\d/.test(text.charAt(i))) {
            digitsFound++;
            if (digitsFound === digitCount) {
              return i + 1;
            }
          }
        }
        return text.length;
      };
      
      const CustomInputComponent = React.useCallback(
        React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(
          ({ className: inputClassName, ...inputProps }, inputForwardedRef) => {
            React.useImperativeHandle(inputForwardedRef, () => inputRef.current!);
            
            const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
              if (inputRef.current) {
                const currentPosition = inputRef.current.selectionStart || 0;
                const currentValue = inputRef.current.value;
                
                const digitsBeforeCursor = countDigitsBeforePosition(currentValue, currentPosition);
                
                cursorPosRef.current = digitsBeforeCursor;
                prevValueRef.current = currentValue;
              }
              inputProps.onKeyDown?.(e);
            };

            const handleInput = (e: React.FormEvent<HTMLInputElement>) => {
              if (inputRef.current) {
                const currentPosition = inputRef.current.selectionStart || 0;
                const currentValue = inputRef.current.value;
                
                const digitsBeforeCursor = countDigitsBeforePosition(currentValue, currentPosition);
                
                cursorPosRef.current = digitsBeforeCursor;
                prevValueRef.current = currentValue;
              }
              inputProps.onInput?.(e);
            };

            const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
              if (inputRef.current) {
                const currentPosition = inputRef.current.selectionStart || 0;
                const currentValue = inputRef.current.value;
                
                const digitsBeforeCursor = countDigitsBeforePosition(currentValue, currentPosition);
                
                cursorPosRef.current = digitsBeforeCursor;
                prevValueRef.current = currentValue;
              }
              inputProps.onClick?.(e);
            };

            return (
              <Input
                {...inputProps}
                ref={inputRef}
                className={cn("rounded-e-lg rounded-s-none", inputClassName)}
                onKeyDown={handleKeyDown}
                onInput={handleInput}
                onClick={handleClick}
              />
            );
          },
        ),
        [],
      );

      const handleChange = React.useCallback((value: RPNInput.Value) => {
        onChange?.(value || ("" as RPNInput.Value));
        
        if (cursorPosRef.current !== null && inputRef.current) {
          const targetDigitPosition = cursorPosRef.current;
          
          setTimeout(() => {
            if (inputRef.current) {
              const newValue = inputRef.current.value;
              
              const newPosition = findPositionAfterDigits(newValue, targetDigitPosition);
              
              const safePosition = Math.min(newPosition, newValue.length);
              
              inputRef.current.setSelectionRange(safePosition, safePosition);
            }
          }, 0);
        }
      }, [onChange]);

      return (
        <div className="flex flex-col w-full gap-2">
          {label && <p>{label}</p>}
          <RPNInput.default
            ref={ref}
            className={cn("flex", className)}
            flagComponent={FlagComponent}
            countrySelectComponent={CountrySelect}
            inputComponent={CustomInputComponent}
            smartCaret={false}
            onChange={handleChange}
            {...props}
          />
          {error && (
            <span className="text-red-500 text-sm">
              {error}
            </span>
          )}
        </div>
      );
    },
  );
PhoneInput.displayName = "PhoneInput";

const InputComponent = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<"input">
>(({ className, ...props }, ref) => {
  return (
    <Input
      className={cn("rounded-e-lg rounded-s-none", className)}
      {...props}
      ref={ref}
    />
  );
});
InputComponent.displayName = "InputComponent";

type CountryEntry = { label: string; value: RPNInput.Country | undefined };

type CountrySelectProps = {
  disabled?: boolean;
  value: RPNInput.Country;
  options: CountryEntry[];
  onChange: (country: RPNInput.Country) => void;
};

const CountrySelect = ({
  disabled,
  value: selectedCountry,
  options: countryList,
  onChange,
}: CountrySelectProps) => {
  return (
    <Popover modal={true}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="flex gap-1 rounded-e-none rounded-s-lg border-r-0 px-3 focus:z-10 h-full"
          disabled={disabled}
        >
          <FlagComponent
            country={selectedCountry}
            countryName={selectedCountry}
          />
          <ChevronsUpDown
            className={cn(
              "-mr-2 size-4 opacity-50",
              disabled ? "hidden" : "opacity-100",
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0">
        <Command>
          <CommandInput placeholder="Search country..." />
          <CommandList>
            <ScrollArea className="h-72">
              <CommandEmpty>No country found.</CommandEmpty>
              <CommandGroup>
                {countryList.map(({ value, label }) =>
                  value ? (
                    <CountrySelectOption
                      key={value}
                      country={value}
                      countryName={label}
                      selectedCountry={selectedCountry}
                      onChange={onChange}
                    />
                  ) : null,
                )}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

interface CountrySelectOptionProps extends RPNInput.FlagProps {
  selectedCountry: RPNInput.Country;
  onChange: (country: RPNInput.Country) => void;
}

const CountrySelectOption = ({
  country,
  countryName,
  selectedCountry,
  onChange,
}: CountrySelectOptionProps) => {
  return (
    <CommandItem className="gap-2" onSelect={() => onChange(country)}>
      <FlagComponent country={country} countryName={countryName} />
      <span className="flex-1 text-sm">{countryName}</span>
      <span className="text-sm text-foreground/50">{`+${RPNInput.getCountryCallingCode(country)}`}</span>
      <CheckIcon
        className={`ml-auto size-4 ${country === selectedCountry ? "opacity-100" : "opacity-0"}`}
      />
    </CommandItem>
  );
};

const FlagComponent = ({ country, countryName }: RPNInput.FlagProps) => {
  const Flag = flags[country];

  return (
    <span className="flex h-4 w-6 items-center justify-center overflow-hidden rounded-sm bg-foreground/20 [&_svg]:size-full">
      {Flag && <Flag title={countryName} />}
    </span>
  );
};

export { PhoneInput };
